/**
 * ChargeSelector Component
 * 
 * Purpose:
 *  These changes were implemented to support **keyboard sequencing** and improve accessibility
 *  by allowing users to navigate and activate invoice choices using only the keyboard.

 *
 * Enhancements:
 * - Added support for auto-focusing the first active invoice on mount.
 *   - Created a `firstActiveRef` using `React.createRef<HTMLDivElement>()`.
 *   - Passed the ref to the first <InvoiceChoice /> element via `ref` prop.
 *   - Used `componentDidMount()` to focus the first invoice if available.
 *
 * - Updated <InvoiceChoice /> component to support receiving a `ref` from parent.
 *   - Converted <InvoiceChoice /> from React.FC to `React.forwardRef`.
 *   - Attached `ref` to the root <div> for DOM access.
 *
 * - Added keyboard accessibility:
 *   - Pressing `Enter` while focused on an invoice triggers `handleAddCharge()`.
 *
 * Note:
 * - `React.forwardRef` is necessary because functional components do not receive `ref` directly.
 * - We only pass the `ref` to the **first** invoice to avoid conflicts.
 */

import React, { DetailedHTMLProps, HTMLAttributes } from 'react';
import classnames from 'classnames';
import pluralize from 'pluralize';
import { cmxDateTime } from '@codametrix/ui-common';

// ---------------------- types  ---------------------- //
export type ChargeSelectorProps = {
  /**
   * The invoice options retrieved from the AmplifyAPI for all invoices.
   * Includes both the invoices as well as the summary, which contains departments
   * and fee schedules.
   */
  invoiceOptions: AmplifyAPI.InvoiceOptionsDTO;
  /**
   * Handler for adding a transaction to a new invoice.
   * @param invoice
   * @returns void
   */
  handleAddCharge: (invoice: AmplifyAPI.Invoice) => void;
  /**
   * Handler for the addition of a new invoice.
   * @param invoices
   * @returns void
   */
  handleInvoiceAddition: (invoices: CMxCommonApp.CodableInvoice[]) => void;
} & DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

type ChargeSelectorState = {
  open: boolean;
};

type InvoiceChoiceProps = {
  invoice: AmplifyAPI.Invoice;
  handleAddCharge: (invoice: AmplifyAPI.Invoice) => void;
};
// ---------------------- utilities  ---------------------- //
// React.forwardRef is used to allow the parent component to attach a ref
// to this functional component, so that the parent can access the underlying DOM node.

const InvoiceChoice = React.forwardRef<HTMLDivElement, InvoiceChoiceProps>(
  ({ invoice, handleAddCharge }, ref) => {
    return (
      <div
        ref={ref}
        onClick={() => handleAddCharge(invoice)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            handleAddCharge(invoice);
          }
        }}
        tabIndex={0}
        className={classnames('qa-invoice-box invoice-box', {
          'invoice-removed': invoice.deleted
        })}>
        <p className="invoice-box-title">
          {invoice.billing_provider_name}
          {invoice.billing_location && ` at ${invoice.billing_location}`}
        </p>
        <small className="invoice-box-subtitle">
          {invoice.date_of_service
            ? cmxDateTime.format(
                invoice.date_of_service,
                cmxDateTime.FORMATS.LONG_DATE
              )
            : 'Date not available'}
          {invoice.deleted && <span> - Previously removed charge</span>}
        </small>
      </div>
    );
  }
);

class ChargeSelector extends React.Component<
  ChargeSelectorProps,
  ChargeSelectorState
> {
  static displayName = 'ChargeSelector';
  firstActiveRef: React.RefObject<HTMLDivElement | null>;

  constructor(props: ChargeSelectorProps) {
    super(props);
    this.handleOpenClick.bind(this);
    // Create a ref to focus the first active invoice when the component mounts.
    // This ref is passed to the first <InvoiceChoice /> component.
    this.firstActiveRef = React.createRef<HTMLDivElement>();
    this.state = { open: false };
  }

  componentDidMount() {
    // On mount, if the ref is attached, focus the first active invoice.
    if (this.firstActiveRef.current) {
      this.firstActiveRef.current?.focus();
    }
  }

  handleOpenClick(event: React.MouseEvent) {
    event.preventDefault();
    const state = { ...this.state };
    state.open = !state.open;
    this.setState(state);
  }

  render(): React.ReactNode {
    const {
      invoiceOptions,
      handleAddCharge,
      handleInvoiceAddition,
      className,
      ...rest
    } = this.props;
    const { invoices } = invoiceOptions;
    const styleClasses = classnames(['charge-selector', className]);
    const activeInvoices = invoices.filter(invoice => !invoice.deleted);
    const inactiveInvoices = invoices.filter(invoice => invoice.deleted);

    return (
      <div className={styleClasses}>
        {activeInvoices.map((invoice, idx) => (
          // Pass the `ref` only to the first <InvoiceChoice /> so it can be focused on mount.
          // Since InvoiceChoice is a functional component, we wrapped it in React.forwardRef.
          <InvoiceChoice
            key={idx}
            invoice={invoice}
            handleAddCharge={handleAddCharge}
            ref={idx === 0 ? this.firstActiveRef : undefined}
          />
        ))}
        <div
          tabIndex={0}
          onClick={() =>
            handleInvoiceAddition(invoices as CMxCommonApp.CodableInvoice[])
          }
          onKeyDown={e => {
            if (e.key === 'Enter') {
              handleInvoiceAddition(invoices as CMxCommonApp.CodableInvoice[]);
            }
          }}
          key={'qa-invoice-new-invoice'}
          className="qa-invoice-box qa-new-invoice invoice-box">
          <p className="invoice-box-title">
            A different provider or date of service.
          </p>
        </div>
        {inactiveInvoices.length > 0 && (
          <>
            <small>
              <a
                href="#show"
                className="qa-show-all"
                onClick={e => this.handleOpenClick(e)}>
                {this.state.open ? 'Hide' : 'Show'}{' '}
                {`${inactiveInvoices.length}`} previous removed{' '}
                {pluralize('charge', inactiveInvoices.length)}.
              </a>
            </small>
            {this.state.open &&
              inactiveInvoices.map((invoice, idx) => (
                <InvoiceChoice
                  key={idx}
                  invoice={invoice}
                  handleAddCharge={handleAddCharge}
                />
              ))}
          </>
        )}
      </div>
    );
  }
}

export { ChargeSelector };
